/**
 * Fully generic common form component.
 * Props:
 * - fields: FieldConfig[] (array of field configs)
 * - onSubmit: (values: Record<string, any>) => void
 * - onCancel: () => void (optional)
 * - buttons: ButtonConfig[] (optional, for custom buttons)
 * - validate?: (values: Record<string, any>) => Record<string, string | undefined> (optional, for whole-form validation)
 *
 * FieldConfig:
 *   - name: string
 *   - label: string
 *   - type: 'text' | 'number' | 'select' | ...
 *   - placeholder?: string
 *   - options?: { label: string, value: any }[] (for select)
 *   - validate?: (value: any, values: Record<string, any>) => string | undefined
 *   - required?: boolean
 *   - defaultValue?: any
 *
 * ButtonConfig:
 *   - text: string
 *   - type?: 'submit' | 'button' | 'reset'
 *   - variant?: string (bootstrap variant)
 *   - onClick?: (values: Record<string, any>) => void
 *   - disabled?: boolean
 */
import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";

export type FieldConfig = {
  name: string;
  label: string;
  type: "text" | "number" | "select" | "password" | "email";
  placeholder?: string;
  options?: { label: string; value: any }[]; // for select
  validate?: (value: any, values: Record<string, any>) => string | undefined;
  required?: boolean;
  defaultValue?: any;
  helperText?: string | React.ReactNode;
};

export type ButtonConfig = {
  text: string;
  type?: "submit" | "button" | "reset";
  variant?: string;
  onClick?: (values: Record<string, any>) => void;
  disabled?: boolean;
};

export interface CommonFormProps {
  fields: FieldConfig[];
  onSubmit: (values: Record<string, any>) => void;
  onCancel?: () => void;
  buttons?: ButtonConfig[];
  validate?: (values: Record<string, any>) => Record<string, string | undefined>;
}

const CommonForm = ({
  fields,
  onSubmit,
  onCancel,
  buttons,
  validate,
}: CommonFormProps): JSX.Element => {
  // Initialize form state with default values
  const initialState = fields.reduce((acc, field) => {
    acc[field.name] = field.defaultValue ?? "";
    return acc;
  }, {} as Record<string, any>);

  const [values, setValues] = useState<Record<string, any>>(initialState);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  // Per-field validation
  const validateField = (field: FieldConfig, value: any) => {
    if (field.required && (value === undefined || value === "")) {
      return `${field.label} is required.`;
    }
    if (field.validate) {
      return field.validate(value, values);
    }
    return undefined;
  };

  // Validate all fields
  const validateAll = (vals: Record<string, any>) => {
    let newErrors: Record<string, string | undefined> = {};
    fields.forEach((field) => {
      const err = validateField(field, vals[field.name]);
      if (err) newErrors[field.name] = err;
    });
    if (validate) {
      const formErrors = validate(vals);
      newErrors = { ...newErrors, ...formErrors };
    }
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setValues((prev) => ({ ...prev, [name]: type === "number" ? Number(value) : value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    // Validate on change
    const field = fields.find((f) => f.name === name);
    if (field) {
      setErrors((prev) => ({ ...prev, [name]: validateField(field, value) }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const field = fields.find((f) => f.name === name);
    if (field) {
      setErrors((prev) => ({ ...prev, [name]: validateField(field, value) }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateAll(values);
    setErrors(newErrors);
    setTouched(
      fields.reduce((acc, field) => {
        acc[field.name] = true;
        return acc;
      }, {} as Record<string, boolean>)
    );
    if (Object.values(newErrors).every((err) => !err)) {
      onSubmit(values);
    }
  };

  return (
    <Form className="d-inline-block w-100" onSubmit={handleSubmit}>
      <div className="form-box">
        {fields.map((field) => (
          <Form.Group key={field.name}>
            <Form.Label htmlFor={field.name}>{field.label}</Form.Label>
            {field.type === "select" ? (
              <Form.Control
                as="select"
                name={field.name}
                id={field.name}
                value={values[field.name]}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched[field.name] && !!errors[field.name]}
                required={field.required}
              >
                <option value="">Select...</option>
                {field.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Control>
            ) : (
              <Form.Control
                type={field.type}
                name={field.name}
                id={field.name}
                value={values[field.name]}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched[field.name] && !!errors[field.name]}
                placeholder={field.placeholder}
                required={field.required}
              />
            )}
            <Form.Control.Feedback type="invalid" className="mt-2">
              {errors[field.name]}
            </Form.Control.Feedback>
            {field.helperText && (
              <Form.Text className="text-muted mt-2 d-block" style={{ fontSize: '0.8rem' }}>
                {field.helperText}
              </Form.Text>
            )}
          </Form.Group>
        ))}
      </div>
      <div>
        {buttons
          ? buttons.map((btn, idx) => {
            const isFormInvalid = fields.some(field => field.required && !values[field.name]) ||
              Object.values(errors).some(Boolean);
            const defaultDisabled = btn.type === "submit" ? isFormInvalid : false;
            const shouldDisable = btn.disabled ?? defaultDisabled;
            return (
              <Button
                key={btn.text + idx}
                type={btn.type ?? "button"}
                variant={btn.variant ?? (btn.type === "submit" ? "primary" : "secondary")}
                className="mt-4 mr-2"
                onClick={
                  btn.type === "submit"
                    ? undefined
                    : () => btn.onClick?.(values)
                }
                disabled={shouldDisable}
              >
                {btn.text}
              </Button>
            );
          })
          : (
            <>
              <Button
                variant="secondary"
                className="mt-4 mr-2"
                type="button"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="mt-4"
                type="submit"
                disabled={
                  fields.some(field => field.required && !values[field.name]) ||
                  Object.values(errors).some(Boolean)
                }
              >
                Submit
              </Button>
            </>
          )}
      </div>
    </Form>
  );
};

export default CommonForm; 